import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Printer, Loader2, Save } from 'lucide-react';
import { contractsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const DIETARY_KEYS = [
  ['dietary_vegan', 'Vegan'],
  ['dietary_vegetarian', 'Vegetarian'],
  ['dietary_gluten_free', 'Gluten-Free'],
  ['dietary_nut_allergy', 'Nut Allergy'],
  ['dietary_dairy_free', 'Dairy-Free'],
  ['dietary_halal', 'Halal'],
  ['dietary_kosher', 'Kosher'],
];

const fmtTime = (t) => {
  if (!t) return '';
  const [hStr, mStr] = String(t).split(':');
  let h = parseInt(hStr);
  const m = mStr || '00';
  if (isNaN(h)) return t;
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
};

/** Broader auto-detect: coffee/tea keywords anywhere in the contract text. */
const detectsCoffee = (c) => {
  const bev = c.plated_beverages;
  if (bev && typeof bev === 'object') {
    for (const key of Object.keys(bev)) {
      const item = bev[key];
      const name = (item?.name || '').toLowerCase();
      if (/coffee|tea|espresso|decaf/.test(name) && (item?.quantity || 0) > 0) return true;
    }
  }
  const haystack = [
    c.custom_menu_beverage, c.custom_menu_dessert, c.custom_menu_dinner, c.custom_menu_cocktail,
    c.custom_menu_late_night, c.custom_menu, c.special_requests, c.kitchen_notes, c.notes,
    ...(c.selected_hors_doeuvres || []).map(i => i.name),
    ...(c.selected_buffet || []).map(i => i.name),
    ...(c.selected_plated_mains || []).map(i => i.item?.name || i.name),
    ...(c.custom_menu_beverage_tiers || []).map(t => t?.label),
    ...(c.custom_menu_dessert_tiers || []).map(t => t?.label),
    ...(c.custom_menu_dinner_tiers || []).map(t => t?.label),
  ].map(v => (v || '').toLowerCase()).join(' ');
  if (/\b(coffee|tea|espresso|decaf)\b/.test(haystack)) return true;
  return false;
};

const dessertDetail = (c) => {
  const parts = [];
  if (c.selected_plated_dessert?.name) parts.push(c.selected_plated_dessert.name);
  if (c.custom_menu_dessert) parts.push(c.custom_menu_dessert);
  (c.custom_menu_dessert_tiers || []).forEach(t => { if (t?.label) parts.push(t.label); });
  return parts.filter(Boolean).join(' • ');
};

const menuSummary = (c) => {
  const lines = [];
  if (c.selected_plated_appetizer?.name) lines.push(`App: ${c.selected_plated_appetizer.name}`);
  if (c.selected_plated_salad?.name) lines.push(`Salad: ${c.selected_plated_salad.name}`);
  (c.selected_plated_mains || []).forEach(m => {
    lines.push(`${m.quantity || ''} ${m.item?.name || m.name || ''}`.trim());
  });
  (c.selected_buffet || []).forEach(b => lines.push(b.name));
  if (c.custom_menu_dinner) lines.push(c.custom_menu_dinner);
  (c.custom_menu_dinner_tiers || []).forEach(t => t?.label && lines.push(t.label));
  return lines.filter(Boolean);
};

const horsDetail = (c) => {
  const items = (c.selected_hors_doeuvres || []).map(h => h.name).filter(Boolean);
  const custom = (c.custom_menu_cocktail || '').trim();
  const list = [...items];
  if (custom) list.push(custom);
  return list;
};

const dietarySummary = (c) => {
  const parts = DIETARY_KEYS
    .map(([k, label]) => (parseInt(c[k]) > 0 ? `${label}: ${c[k]}` : null))
    .filter(Boolean);
  const special = (c.dietary_special_meals || c.dietary_notes || '').trim();
  if (special) parts.push(special);
  return parts;
};

/** Build the initial edit-state for one contract from its stored data. */
const buildInitialEvent = (c) => {
  const hors = horsDetail(c);
  const menu = menuSummary(c);
  const service = c.service_style === 'plated' ? 'Plated'
    : c.service_style === 'plated_lunch' ? 'Plated Lunch'
    : c.service_style === 'buffet' ? 'Buffet'
    : c.service_style === 'breakfast_buffet' ? 'Breakfast Buffet'
    : c.service_style === 'lunch_buffet' ? 'Lunch Buffet'
    : c.service_style === 'dinner_buffet' ? 'Dinner Buffet'
    : (c.service_style ? c.service_style.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '');
  const chafing = c.chafing_dish_count ? String(c.chafing_dish_count) : '';
  return {
    address: c.location || '',
    cocktailTime: fmtTime(c.cocktail_hour_time),
    cocktailYes: c.cocktail_hour !== null && c.cocktail_hour !== undefined ? !!c.cocktail_hour : hors.length > 0,
    cocktailService: c.hors_doeuvres_service || '',
    cocktailNotes: hors.join('\n'),
    menuTime: fmtTime(c.dinner_start_time),
    menuText: menu.join('\n'),
    serviceStyle: service,
    chafingCount: chafing,
    coffeeYes: c.coffee_service !== null && c.coffee_service !== undefined ? !!c.coffee_service : detectsCoffee(c),
    coffeeNotes: c.coffee_notes || (c.coffee_decanters != null ? `${c.coffee_decanters} decanters` : ''),
    departureTime: c.departure_time ? fmtTime(c.departure_time) : '',
    cocktailYes: c.cocktail_hour !== null && c.cocktail_hour !== undefined ? !!c.cocktail_hour : hors.length > 0,
    dessertYes: !!dessertDetail(c),
    dessertText: dessertDetail(c),
    dessertPlatedOrBuffet: service,
    driveTime: '',
    specialNotes: c.special_requests || c.kitchen_notes || '',
    dietary: dietarySummary(c).join('\n'),
  };
};

const storageKey = (date) => `club_services_rollup_${date}`;

const Row = ({ label, children }) => (
  <tr className="border-b border-black align-top">
    <th className="w-28 bg-gray-100 text-left align-top px-2 py-1 text-[10px] font-bold uppercase text-[#1e3a5f] border-r border-black">{label}</th>
    <td className="px-2 py-1 text-xs">{children}</td>
  </tr>
);

/** Small editable field.  Persists via onChange (parent debounces the save). */
const EditableText = ({ value, onChange, placeholder, multiline, testid }) => {
  if (multiline) {
    return (
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full text-xs resize-none bg-transparent border-0 focus:outline-none focus:bg-yellow-50 print:bg-transparent print:resize-none leading-tight"
        rows={Math.max(1, (value || '').split('\n').length)}
        data-testid={testid}
      />
    );
  }
  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full text-xs bg-transparent border-0 focus:outline-none focus:bg-yellow-50 print:bg-transparent"
      data-testid={testid}
    />
  );
};

const EventColumn = ({ contract, event, setField }) => {
  const set = (k) => (v) => setField(contract.id, k, v);
  return (
    <div className="border-2 border-black flex-1 min-w-0" data-testid={`rollup-event-${contract.id}`}>
      <div className="bg-[#1e3a5f] text-white px-2 py-1">
        <p className="text-xs font-bold uppercase tracking-wide truncate">{contract.client_name}</p>
        <p className="text-[10px] opacity-80 truncate">{contract.event_name} · {contract.guest_count || '?'} guests</p>
      </div>
      <table className="w-full text-xs">
        <tbody>
          <Row label="Address">
            <EditableText value={event.address} onChange={set('address')} placeholder="Offsite address" testid={`address-${contract.id}`} />
          </Row>

          <Row label="Cocktail Hour">
            <div className="flex items-center gap-2 mb-1">
              <select value={event.cocktailYes ? 'yes' : 'no'} onChange={(e) => set('cocktailYes')(e.target.value === 'yes')} className="text-[11px] bg-transparent border-b border-gray-300 focus:outline-none focus:bg-yellow-50" data-testid={`cocktail-yes-${contract.id}`}>
                <option value="yes">YES</option>
                <option value="no">NO</option>
              </select>
              {event.cocktailYes && (
                <>
                  <span className="text-[10px] text-gray-500">Start:</span>
                  <input type="text" value={event.cocktailTime} onChange={(e) => set('cocktailTime')(e.target.value)} placeholder="6:00 PM" className="text-xs w-20 bg-transparent border-b border-gray-300 focus:outline-none focus:bg-yellow-50" data-testid={`cocktail-time-${contract.id}`} />
                  <select value={event.cocktailService} onChange={(e) => set('cocktailService')(e.target.value)} className="text-[11px] bg-transparent border-b border-gray-300 focus:outline-none" data-testid={`cocktail-service-${contract.id}`}>
                    <option value="">stationed/passed?</option>
                    <option value="stationed">Stationed</option>
                    <option value="passed">Passed</option>
                  </select>
                </>
              )}
            </div>
            {event.cocktailYes && (
              <EditableText value={event.cocktailNotes} onChange={set('cocktailNotes')} placeholder="One item per line" multiline testid={`cocktail-notes-${contract.id}`} />
            )}
          </Row>

          <Row label="Menu">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] text-gray-500">Service:</span>
              <input type="text" value={event.menuTime} onChange={(e) => set('menuTime')(e.target.value)} placeholder="7:30 PM" className="text-xs w-20 bg-transparent border-b border-gray-300 focus:outline-none focus:bg-yellow-50" data-testid={`menu-time-${contract.id}`} />
            </div>
            <EditableText value={event.menuText} onChange={set('menuText')} placeholder="Menu items, one per line" multiline testid={`menu-text-${contract.id}`} />
          </Row>

          <Row label="Service">
            <div className="flex items-center gap-2">
              <select value={event.serviceStyle} onChange={(e) => set('serviceStyle')(e.target.value)} className="text-xs bg-transparent border-b border-gray-300 focus:outline-none" data-testid={`service-style-${contract.id}`}>
                <option value="">—</option>
                <option value="Plated">Plated</option>
                <option value="Buffet">Buffet</option>
                <option value="Family Style">Family Style</option>
                <option value="Stations">Stations</option>
              </select>
              {event.serviceStyle === 'Buffet' && (
                <span className="text-[11px]">Chafing dishes: <input type="number" min="0" value={event.chafingCount} onChange={(e) => set('chafingCount')(e.target.value)} className="text-xs w-16 bg-transparent border-b border-gray-300 focus:outline-none focus:bg-yellow-50" data-testid={`chafing-count-${contract.id}`} /></span>
              )}
            </div>
          </Row>

          <Row label="Departure Time">
            <EditableText value={event.departureTime} onChange={set('departureTime')} placeholder="e.g. 3:30 PM (when to leave the club)" testid={`departure-${contract.id}`} />
          </Row>

          <Row label="Coffee / Tea">
            <div className="flex items-center gap-2 mb-1">
              <label className="flex items-center gap-1 text-[11px]">
                <input type="checkbox" checked={event.coffeeYes} onChange={(e) => set('coffeeYes')(e.target.checked)} data-testid={`coffee-yes-${contract.id}`} />
                YES
              </label>
            </div>
            {event.coffeeYes && (
              <EditableText value={event.coffeeNotes} onChange={set('coffeeNotes')} placeholder="What they need — e.g. 3 decanters coffee, 2 carafes hot water, cream & sugar setup" multiline testid={`coffee-notes-${contract.id}`} />
            )}
          </Row>

          <Row label="Dessert">
            <div className="flex items-center gap-2 mb-1">
              <label className="flex items-center gap-1 text-[11px]">
                <input type="checkbox" checked={event.dessertYes} onChange={(e) => set('dessertYes')(e.target.checked)} data-testid={`dessert-yes-${contract.id}`} />
                YES
              </label>
              {event.dessertYes && (
                <select value={event.dessertPlatedOrBuffet} onChange={(e) => set('dessertPlatedOrBuffet')(e.target.value)} className="text-[11px] bg-transparent border-b border-gray-300 focus:outline-none" data-testid={`dessert-style-${contract.id}`}>
                  <option value="">plated/buffet?</option>
                  <option value="Plated">Plated</option>
                  <option value="Buffet">Buffet</option>
                </select>
              )}
            </div>
            {event.dessertYes && (
              <EditableText value={event.dessertText} onChange={set('dessertText')} placeholder="What's for dessert?" multiline testid={`dessert-text-${contract.id}`} />
            )}
          </Row>

          <Row label="Drive Time">
            <EditableText value={event.driveTime} onChange={set('driveTime')} placeholder="Driver fills in on-site" testid={`drive-time-${contract.id}`} />
          </Row>

          <Row label="Special Notes">
            <EditableText value={event.specialNotes} onChange={set('specialNotes')} multiline testid={`special-notes-${contract.id}`} />
          </Row>

          <Row label="Dietary / Special Meals">
            <EditableText value={event.dietary} onChange={set('dietary')} placeholder="One per line" multiline testid={`dietary-${contract.id}`} />
          </Row>
        </tbody>
      </table>
    </div>
  );
};

const chunkPairs = (arr) => {
  const out = [];
  for (let i = 0; i < arr.length; i += 2) out.push(arr.slice(i, i + 2));
  return out;
};

const ClubServicesRollup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const initialDate = new URLSearchParams(location.search).get('date') || new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(initialDate);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState({});  // { [contract_id]: EventState }
  const [departureTime, setDepartureTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);

  // Load contracts once
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    contractsAPI.getAll()
      .then(all => { if (!cancelled) setContracts(all || []); })
      .catch(() => { if (!cancelled) toast({ title: 'Failed to load contracts', variant: 'destructive' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const offsiteForDate = useMemo(() => {
    return contracts
      .filter(c => c.event_date === date)
      .filter(c => c.location_type === 'offsite' || c.event_type === 'offsite')
      .sort((a, b) => (a.event_time || '').localeCompare(b.event_time || ''));
  }, [contracts, date]);

  // Rebuild edit state whenever the date / contract set changes.  Merge saved
  // overrides from localStorage so the user's manual edits stick across reloads.
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey(date)) || '{}');
      const nextEvents = {};
      for (const c of offsiteForDate) {
        const base = buildInitialEvent(c);
        const override = saved.events?.[c.id] || {};
        nextEvents[c.id] = { ...base, ...override };
      }
      setEvents(nextEvents);
      setDepartureTime(saved.departureTime || '');
    } catch {
      setEvents({});
      setDepartureTime('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, offsiteForDate.length, contracts]);

  // Persist edits to localStorage (debounced so we don't hit storage on every keystroke)
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(storageKey(date), JSON.stringify({ departureTime, events }));
      } catch { /* quota */ }
    }, 250);
    return () => clearTimeout(t);
  }, [date, departureTime, events, loading]);

  // Auto-save to server 3 s after last edit — silent background sync so
  // the user never has to remember to click the button. Only fires while
  // dirty AND when there are 2+ offsite events for this date.
  useEffect(() => {
    if (loading || !dirty || offsiteForDate.length < 2) return;
    const t = setTimeout(() => { saveToServer(); }, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, events, loading, offsiteForDate.length]);

  const setField = useCallback((contractId, key, value) => {
    setDirty(true);
    setEvents(prev => ({
      ...prev,
      [contractId]: { ...(prev[contractId] || {}), [key]: value },
    }));
  }, []);

  const resetOverrides = () => {
    if (!window.confirm('Reset all manual edits for this date back to the auto-filled contract data?')) return;
    try { localStorage.removeItem(storageKey(date)); } catch { /* ignore */ }
    const next = {};
    for (const c of offsiteForDate) next[c.id] = buildInitialEvent(c);
    setEvents(next);
    setDepartureTime('');
    toast({ title: 'Edits cleared' });
  };

  // Parse a user-typed time like "6:30 PM" or "18:30" back to 24h "HH:MM"
  const parse24h = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim().toUpperCase();
    const m = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = m[2];
    const ap = m[3];
    if (ap === 'PM' && h < 12) h += 12;
    if (ap === 'AM' && h === 12) h = 0;
    if (h < 0 || h > 23 || parseInt(min) > 59) return null;
    return `${String(h).padStart(2, '0')}:${min}`;
  };

  const saveToServer = async () => {
    if (saving) return;
    setSaving(true);
    let ok = 0;
    let failed = 0;
    for (const c of offsiteForDate) {
      const e = events[c.id];
      if (!e) continue;
      const payload = {
        location: e.address || null,
        hors_doeuvres_service: e.cocktailService || null,
        cocktail_hour: !!e.cocktailYes,
        chafing_dish_count: e.chafingCount === '' ? null : (parseInt(e.chafingCount) || null),
        coffee_service: !!e.coffeeYes,
        coffee_notes: e.coffeeNotes || null,
        cocktail_hour_time: parse24h(e.cocktailTime),
        dinner_start_time: parse24h(e.menuTime),
        departure_time: parse24h(e.departureTime),
        service_style: e.serviceStyle ? e.serviceStyle.toLowerCase().replace(/\s+/g, '_') : null,
        custom_menu_dessert: e.dessertYes && e.dessertText ? e.dessertText : null,
        special_requests: e.specialNotes || null,
      };
      // Only persist menu text if the user actually edited it away from the auto-derived summary.
      const originalMenu = menuSummary(c).join('\n');
      if (e.menuText && e.menuText.trim() !== originalMenu.trim()) {
        payload.custom_menu_dinner = e.menuText;
      }
      // Drop nulls so we don't wipe unrelated fields on legacy contracts.
      const cleaned = Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== null && v !== undefined && v !== ''));
      try {
        await contractsAPI.update(c.id, cleaned);
        ok += 1;
      } catch (err) {
        failed += 1;
      }
    }
    setSaving(false);
    setDirty(false);
    setLastSavedAt(new Date());
    if (failed === 0) {
      // silent success — status pill shows "Saved ✓"
    } else {
      toast({
        title: `Saved ${ok}, failed ${failed}`,
        description: 'Retry or check the network tab for details.',
        variant: 'destructive',
      });
    }
  };

  const pairs = chunkPairs(offsiteForDate);

  const handlePrint = () => {
    const el = document.getElementById('rollup-printable');
    if (!el) return;
    const w = window.open('', '_blank', 'width=1100,height=850');
    if (!w) { alert('Pop-up blocked. Allow pop-ups to print.'); return; }
    const styleLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map(l => l.outerHTML).join('\n');
    const styleTags = Array.from(document.querySelectorAll('style')).map(s => `<style>${s.innerHTML}</style>`).join('\n');
    w.document.open();
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Club Services Roll-Up ${date}</title>${styleLinks}${styleTags}
      <style>
        @page { size: letter landscape; margin: 0.35in; }
        body { background: #fff; margin: 0; font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; color: #111; }
        .page-break { page-break-after: always; }
        .page-break:last-child { page-break-after: auto; }
        input, textarea, select { border: 0 !important; background: transparent !important; -webkit-appearance: none; appearance: none; }
        select::-ms-expand { display: none; }
      </style></head><body>${el.outerHTML}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch (e) { /* user cancelled */ } }, 350);
  };

  if (loading) {
    return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-[#1e3a5f]" size={48} /></div>;
  }

  return (
    <div className="p-4 sm:p-6 bg-[#faf9f6] min-h-screen" data-testid="club-services-rollup">
      <div className="print:hidden flex flex-wrap items-center gap-3 mb-4">
        <Button variant="outline" onClick={() => navigate('/weekly-sheet')} data-testid="rollup-back-btn">
          <ArrowLeft size={16} className="mr-1" />Back
        </Button>
        <h1 className="text-xl font-bold text-[#1e3a5f]">Club Services Roll-Up</h1>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="border-2 border-gray-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-[#1e3a5f]"
          data-testid="rollup-date-input"
        />
        <span className="text-sm text-gray-600" data-testid="rollup-count">
          {offsiteForDate.length} offsite event{offsiteForDate.length === 1 ? '' : 's'}
        </span>
        {offsiteForDate.length >= 2 && (
          <>
            <Button variant="outline" onClick={resetOverrides} className="text-red-600 hover:bg-red-50 border-red-200" data-testid="rollup-reset-btn">
              Reset edits
            </Button>
            <Button onClick={saveToServer} disabled={saving} className="bg-[#2d5016] hover:bg-[#3a6a1f] text-white" data-testid="rollup-save-btn" title="Persist these edits back onto the underlying contracts so they show up on the BEO and Weekly Sheet">
              {saving ? (<><Loader2 size={16} className="animate-spin mr-1" />Saving…</>) : (<><Save size={16} className="mr-1" />Save now</>)}
            </Button>
            <span className="text-xs text-gray-500 min-w-max" data-testid="rollup-save-status">
              {saving ? 'Saving…' : dirty ? 'Unsaved changes · auto-saves in 3 s' : (lastSavedAt ? `Saved ✓ ${lastSavedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'Auto-saves')}
            </span>
            <Button onClick={handlePrint} className="ml-auto bg-[#c9a227] hover:bg-[#b8931e] text-[#1e3a5f] font-semibold" data-testid="rollup-print-btn">
              <Printer size={16} className="mr-1" />Print
            </Button>
          </>
        )}
      </div>

      {offsiteForDate.length < 2 ? (
        <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-8 text-center" data-testid="rollup-empty-state">
          <p className="text-gray-600">
            {offsiteForDate.length === 0
              ? 'No offsite events booked on this date.'
              : 'Only 1 offsite event on this date — a roll-up is only generated when there are 2 or more.'}
          </p>
        </div>
      ) : (
        <div id="rollup-printable" className="bg-white p-4 rounded shadow print:shadow-none print:p-0">
          {pairs.map((pair, pageIdx) => (
            <div key={pageIdx} className={`${pageIdx < pairs.length - 1 ? 'page-break' : ''} mb-6`}>
              <div className="text-center mb-2">
                <h2 className="text-lg font-bold text-[#1e3a5f]">CLUB SERVICES ROLL-UP</h2>
                <p className="text-sm text-gray-600">{new Date(date + 'T00:00').toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} · Page {pageIdx + 1} of {pairs.length}</p>
              </div>
              {pageIdx === 0 && (
                <div className="print:hidden text-center text-[11px] text-gray-500 italic mb-2" data-testid="rollup-departure-note">
                  Departure Time is set per event below · Edits auto-save to the server
                </div>
              )}
              <div className="flex gap-3 items-stretch">
                {pair.map(c => (
                  <EventColumn
                    key={c.id}
                    contract={c}
                    event={events[c.id] || buildInitialEvent(c)}
                    setField={setField}
                  />
                ))}
                {pair.length === 1 && <div className="flex-1" />}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClubServicesRollup;
